import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { InputAdornment } from '@mui/material';
import { Add, Delete as DeleteIcon, Search } from '@mui/icons-material';
import { DataGrid, DataGridColDef } from '../../../shared/DataGrid';
import { useData } from '../../../../data-layer';
import { useLocales } from '../../../../hooks';
import { usePermissions } from '../../../../hooks/Permissions/usePermissions';
import { usePermissionsGuard } from '../../../../hooks/General/usePermissionsGuard';
import { AppRoutes } from '../../../../Routes';
import { PermissionsGroupId } from '../../../../API';
import { HomepageOptions } from '../../../../state/theme';
import IconButton from '../../../shared/IconButton';
import Button from '../../../shared/Button';
import TextField from '../../../shared/TextField';
import { withPromotionsSplitPane } from '../../../../state/General';
import { PlanPickerResponse } from '../../../../API';
import { planPickerTestIds } from '../../../shared/TestsIds';
import CountryBadge from '../../../shared/CountryBadge';
import { PlatformBadge } from '../../../shared/Platforms/PlatformBadge';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: theme.spacing(4)
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(4)
  },
  textFilterContainer: {
    flexGrow: 1,
    display: 'flex',
    gap: theme.spacing(4)
  },
  textFilter: {
    width: 500,
    marginBottom: '0 !important'
  },
  gridContainer: {
    display: 'flex',
    flexGrow: 1
  },
  deleteButton: {
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    }
  }
}));

interface Filters {
  text: string;
  country: string;
}

export function PlanPickerManagerComponent(): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const navigate = useNavigate();
  const {
    planPicker: {
      state: { withAllRecords: withAllPlanPickers },
      hook: planPickerHook
    }
  } = useData();

  const planPickers = useRecoilValue(withAllPlanPickers);
  const [filters, setFilters] = useState<Filters>({ text: '', country: '' });
  const [filteredPlanPickers, setFilteredPlanPickers] = useState<PlanPickerResponse[] | undefined>();

  const { canSave, canDelete, canRead } = usePermissionsGuard({
    homepageOption: HomepageOptions.MONETIZATION
  });
  const { hasUpsertPermission, hasDeletePermission, hasReadPermission } = usePermissions();

  const hasMonetizationReadPermissions = hasReadPermission(PermissionsGroupId.MONETIZATION) || canRead;

  const allLoaded = !!planPickers;

  useEffect(() => {
    if (!hasMonetizationReadPermissions) {
      navigate(AppRoutes.welcome);
    }
  }, [hasMonetizationReadPermissions, navigate]);

  useEffect(() => {
    updateFilteredPlanPickers();
  }, [planPickers, filters]);

  function deletePlanPickerHandler(planPicker: PlanPickerResponse) {
    if (window.confirm(t('payments.plan_picker.delete_confirmation'))) {
      planPickerHook.remove(planPicker.id);
    }
  }

  function onPlanPickerClick(planPicker: PlanPickerResponse) {
    planPickerHook.edit(planPicker.id);
  }

  function onTextFilterChanged(event: React.ChangeEvent<HTMLInputElement>) {
    setFilters((prev) => ({ ...prev, text: event.target.value }));
  }

  const updateFilteredPlanPickers = () => {
    if (!planPickers) {
      setFilteredPlanPickers(undefined);
      return;
    }

    let newFilteredPlanPickers = planPickers;

    if (filters.text) {
      const searchText = filters.text.toLowerCase();
      newFilteredPlanPickers = newFilteredPlanPickers.filter(
        (planPicker) =>
          planPicker.country?.toLowerCase().includes(searchText) ||
          planPicker.platforms?.some((platform) => platform.toLowerCase().includes(searchText))
      );
    }

    setFilteredPlanPickers(newFilteredPlanPickers);
  };

  const columns: DataGridColDef<PlanPickerResponse>[] = [
    {
      key: 'country',
      cellRenderer: (planPicker) => {
        if (!planPicker.country) return '-';
        return <CountryBadge countryCode={planPicker.country.toUpperCase()} />;
      },
      label: t('payments.plan_picker.country'),
      sortFunction: (a, b) => (a.country || '').localeCompare(b.country || ''),
      baseWidth: '20%'
    },
    {
      key: 'platforms',
      cellRenderer: (planPicker) => {
        if (!planPicker.platforms || planPicker.platforms.length === 0) return '-';
        return <PlatformBadge platforms={planPicker.platforms} />;
      },
      label: t('payments.plan_picker.platforms'),
      baseWidth: '30%'
    },
    {
      key: 'pricing_configurations',
      cellRenderer: (planPicker) => {
        const count = planPicker.pricing?.length || 0;
        if (count === 0) return '-';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 'bold' }}>{count}</span>
            <span style={{ color: 'text.secondary' }}>
              {t('payments.plan_picker.pricing_configurations_count_label')}
            </span>
          </div>
        );
      },
      label: t('payments.plan_picker.pricing_configurations'),
      baseWidth: '35%'
    },
    {
      key: 'actions',
      cellRenderer: (planPicker) => (
        <IconButton
          size="small"
          className={classes.deleteButton}
          onClick={(e) => {
            e.stopPropagation();
            deletePlanPickerHandler(planPicker);
          }}
          disabled={!hasDeletePermission(PermissionsGroupId.MONETIZATION) && !canDelete}
          data-testid={planPickerTestIds.deleteButton}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
      label: t('general.actions'),
      baseWidth: '15%'
    }
  ];

  return (
    <div className={classes.root} data-testid={planPickerTestIds.root}>
      <div className={classes.filters}>
        <div className={classes.textFilterContainer}>
          <TextField
            clearable
            label={t('payments.plan_picker.filter_by_text')}
            className={classes.textFilter}
            value={filters.text}
            onChange={onTextFilterChanged}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
            data-testid={planPickerTestIds.textFilter}
          />
        </div>
        <Button
          endIcon={<Add />}
          disabled={!allLoaded || (!hasUpsertPermission(PermissionsGroupId.MONETIZATION) && !canSave)}
          onClick={() => planPickerHook.new()}
          data-testid={planPickerTestIds.addButton}
        >
          {t('payments.new_plan_picker')}
        </Button>
      </div>
      <div className={classes.gridContainer}>
        <DataGrid
          rows={filteredPlanPickers}
          columns={columns}
          onRowClick={(planPicker) => onPlanPickerClick(planPicker)}
          withColumnSizes={withPromotionsSplitPane}
          useVirtualization
          rowKey="id"
        />
      </div>
    </div>
  );
}
